import { useSelector } from 'react-redux';
import { Store, useAppDispatch } from 'src/redux/Store';
import { ErrorBag } from 'src/models/ErrorModel';
import { FormControl, TextField } from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { makeStyles } from '@material-ui/core/styles';
import { t } from 'src/lib/language';
import { FORM_ID } from 'src/models/FormModel';
import { isEmpty } from 'lodash';
import { getOrganisationBankAccountAction } from 'src/redux/actions/paymentInfoAction';
import { CSSProperties, useEffect } from 'react';
import { BankAccountModel } from 'src/models/BankAccountModel';

const useStyles = makeStyles(() => ({
  formControl: {
    width: '100%',
    marginBottom: '8px',
  },
  marginTop: {
    marginTop: '16px',
  },
  minWidth: {
    minWidth: '480px',
  },
}));

interface Props {
  disabled?: boolean
  form: FORM_ID,
  name: string,
  title?:string,
  noMarginTop?: boolean
  noMinWidth?: boolean
  onChange: (id:number) => void,
  value?: number,
  style?: CSSProperties,
  margin?: 'dense' | 'normal' | undefined,
}

interface BankAccountItem {
  id: number,
  label?: string,
}

const OrganisationBankAccountSelector = (props: Props) => {
  const classes = useStyles();
  const dispatch = useAppDispatch();

  const {
    disabled, form, name, onChange, value, style, margin, noMarginTop, noMinWidth, title,
  } = props;

  const organisationId = useSelector((state: Store) => state.currentOrganisation.id);
  const bankAccounts = useSelector((state: Store) => state.organisationBankAccounts);
  const lang = useSelector((state: Store) => state.language.language ?? 'en');
  const errors: ErrorBag =
    useSelector((state:Store) => state.errors[form] || {} as ErrorBag);

  useEffect(() => {
    dispatch(getOrganisationBankAccountAction());
  }, []);

  const getBankAccountItems = () => {
    const items:BankAccountItem[] = [{ id: 0, label: undefined }];
    if (isEmpty(bankAccounts)) {
      return items;
    }

    const accounts = bankAccounts[organisationId] as BankAccountModel[] ?? [] as BankAccountModel[];

    accounts.sort(
      (a, b) => a.bank_holder_name.localeCompare(b.bank_holder_name),
    ).forEach((item: any) => {
      items.push({
        id: item.id,
        label: `${item.bank_holder_name} #${item.account}`,
      });
    });
    return items;
  };

  const onValueChange = (item:BankAccountItem) => {
    if (!item || !item.id) {
      onChange(0);
      return null;
    }
    onChange(item.id as number);
    return null;
  };

  const getValue = (initialValue?: number) => {
    let result = {
      id: 0,
      label: undefined,
    } as BankAccountItem;
    if (isEmpty(bankAccounts) || !initialValue) {
      return result;
    }

    const accounts = bankAccounts[organisationId] ?? [];

    accounts.forEach((item: any) => {
      if (item.id === initialValue) {
        result = {
          id: item.id,
          label: `${item.bank_holder_name} #${item.account}`,
        } as BankAccountItem;
      }
    });
    return result;
  };

  const getOptionLabel = (option: BankAccountItem) => option.label || '';

  const getClasses = () => {
    const noMarginTopClass = noMarginTop ? '' : classes.marginTop;
    const noMinWidthClass = noMinWidth ? '' : classes.minWidth;
    return `${noMarginTopClass} ${noMinWidthClass} ${classes.formControl}`;
  };

  const getOptionComponent = (option: BankAccountItem) => (
    <span id={`bank-account-selector-${option.id}`}>{option.label}</span>
  );
  return (
    <FormControl
      id="organisation-bank-account-selector"
      variant="outlined"
      className={classes.formControl}
      key={`form-control-${name}`}
      style={style}>
      <Autocomplete
        disabled={disabled}
        className={getClasses()}
        value={getValue(value)}
        onChange={(e, item) => onValueChange(item as BankAccountItem)}
        options={getBankAccountItems() || []}
        getOptionLabel={(option: BankAccountItem) => getOptionLabel(option)}
        getOptionSelected={(option, item) => option.id === item.id}
        renderInput={(params) => (
          <TextField
            {...params}
            variant="outlined"
            label={title ?? t(lang, 'forms.payments.organisation_bank_accounts')}
            error={!!errors[name]}
            helperText={errors[name] && errors[name].join(',')}
            margin={margin || 'normal'} />
        )}
        renderOption={(option: BankAccountItem) => getOptionComponent(option)}
        key={`autocomplete-${name}`} />
    </FormControl>
  );
};

export default OrganisationBankAccountSelector;
